"use client";

// ── Single root island for /resources ──────────────────────────────────────
// Everything DevLink-related must live inside THIS island. All DevLink
// components mount under ONE <DevLinkProvider> → ONE InteractionsProvider →
// ONE IX2 engine. That constraint is inherited from the source project and is
// not a stylistic preference: per-island providers each spin up their own
// engine, and the engine's runtime mouseover dispatch silently no-ops for some
// action shapes (notably `useEventTarget: "CHILDREN"`, which is exactly what a
// card's hover-arrow animation uses). One island, one engine, hovers work.

import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { DevLinkProvider, type RenderLinkProps } from "@devlink/provider";
import { CaseStudyCardDevlink } from "@devlink/card";
import styles from "@devlink/styles";
import type { FilterValue, ResourceCard, SortValue } from "@/lib/types";
import {
  filterCards,
  filterToSlug,
  parseFilterValue,
  parseSortValue,
  sortCards,
} from "@/lib/resources";
import { FILTER_BUTTONS } from "@/lib/collections";
import { cardPropsFor } from "@/lib/card-props";
import { cls } from "@/lib/cls";

const DEFAULT_PAGE_SIZE = 9;

// ── External control ───────────────────────────────────────────────────────
// The grid owns its state, but on the Webflow page the controls that drive it
// — type tabs, a sort list, a search field — are Designer elements sitting
// OUTSIDE the embed container. Because the embed is same-document rather than
// an iframe, they can reach in directly; this is the surface they reach.
//
// `entry.tsx` hands this to `window.ResourcesEmbed`. Nothing else should use
// it: inside the island, pass props.

/** State snapshot published on `resources-embed:change` and by `getState()`. */
export type ResourcesState = {
  /**
   * The PUBLIC filter slug — "all", "ebooks", "analyst-reports", "webinars",
   * "whitepapers" — not the internal singular `ResourceType`. Typed as a plain
   * string because this crosses into hand-written JS on a Webflow page, and it
   * is what a `data-resource-filter` attribute should be compared against.
   */
  filter: string;
  query: string;
  sort: SortValue;
  /** Cards matching the current filter + query, before pagination. */
  count: number;
  /** Cards loaded in total, ignoring filter and query. */
  total: number;
  /** Cards currently on screen — `count` minus whatever Load more has yet to reveal. */
  visible: number;
};

/**
 * Setters are intentionally string-typed, not union-typed: they are called from
 * hand-written JS in a Webflow custom-code block with no type checking, so each
 * one validates and falls back rather than trusting its caller.
 */
export type ResourcesControls = {
  setFilter: (value: string) => void;
  setQuery: (value: string) => void;
  setSort: (value: string) => void;
  reset: () => void;
  getState: () => ResourcesState;
};

const CHANGE_EVENT = "resources-embed:change";

/**
 * Scope root for every app-owned rule in `globals.css`.
 *
 * Mandatory, not cosmetic. In embed mode this tree renders inside a live Webflow
 * page whose stylesheet defines its own `.category-wrapper`, `.eyebrow` and
 * friends. Every rule the app owns is written as `.ct-resources <selector>`, so
 * app styles cannot escape into the page, and their extra specificity beats the
 * single-class Webflow rules that would otherwise bleed in.
 *
 * Where it lands differs by mode. On the app's own page this component renders
 * it on its `<main>`. In embed mode there is no wrapper to put it on — see the
 * flattening note on `ResourcesIslandProps.embedded` — so `entry.tsx` applies
 * it to the host container instead. Every rule in `globals.css` is a DESCENDANT
 * selector (`.ct-resources .filters-bar`, never `.ct-resources` alone), which is
 * what makes moving it a no-op for styling.
 */
export const SCOPE_CLASS = "ct-resources";

// ── Link resolution ────────────────────────────────────────────────────────
// DevLink exports a Webflow "Page Link" as `href="#"` whenever the target page
// does not exist inside the consumer app — which, for a Webflow Cloud app
// mounted at /resources, is every marketing page on the site. `renderLink` is
// the interception point.
//
// Card links are real URLs built by `buildResourceUrl`, so nothing needs
// rewriting yet. If you later sync the site's Navigation/Footer components,
// this is where their labels get mapped to real paths: build a
// `Record<label, path>` table, pull the link's text out of `children`, and
// resolve `#` through it. The source project's table ran to ~100 entries.
const renderLink: React.FC<RenderLinkProps> = ({
  href,
  target,
  className,
  children,
  ref,
  preload: _preload,
  ...rest
}) => (
  // `preload` is a DevLink/Next-ism; dropping it keeps React from warning about
  // an unknown DOM attribute.
  <a href={href} target={target} className={className} ref={ref} {...rest}>
    {children}
  </a>
);

// ── Filter + search bar ────────────────────────────────────────────────────

type FiltersBarProps = {
  current: FilterValue;
  query: string;
  resultCount: number;
  onFilterChange: (v: FilterValue) => void;
  onQueryChange: (q: string) => void;
};

function FiltersBar({
  current,
  query,
  resultCount,
  onFilterChange,
  onQueryChange,
}: FiltersBarProps) {
  return (
    <div className="filters-bar">
      <div className="filters-bar__buttons" role="group" aria-label="Filter resources by type">
        {FILTER_BUTTONS.map(({ label, value }) => {
          const isActive = current === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={isActive}
              onClick={() => onFilterChange(value)}
              className={
                isActive ? "category-wrapper active-filter" : "category-wrapper"
              }
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="filters-bar__search-wrap">
        <label className="visually-hidden" htmlFor="resources-search">
          Search resources
        </label>
        <input
          id="resources-search"
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search resources…"
          className="filters-bar__search"
        />
        <p className="filters-bar__count" role="status" aria-live="polite">
          {resultCount} {resultCount === 1 ? "resource" : "resources"}
        </p>
      </div>
    </div>
  );
}

// ── Grid ───────────────────────────────────────────────────────────────────

type ResourcesGridProps = {
  initialFilter: FilterValue;
  initialQuery: string;
  initialSort: SortValue;
  allCards: ResourceCard[];
  pageSize: number;
  syncUrl: boolean;
  embedded: boolean;
  onControlsReady?: (controls: ResourcesControls) => void;
};

function ResourcesGrid({
  initialFilter,
  initialQuery,
  initialSort,
  allCards,
  pageSize,
  syncUrl,
  embedded,
  onControlsReady,
}: ResourcesGridProps) {
  const [filter, setFilter] = useState<FilterValue>(initialFilter);
  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState<SortValue>(initialSort);
  const [visibleCount, setVisibleCount] = useState(pageSize);

  // Mirror state into the URL so a filtered view is shareable and survives
  // reload. `replaceState` rather than `pushState`: typing in the search box
  // should not fill the back button with one entry per keystroke. Other query
  // params are preserved.
  //
  // In embed mode this rewrites the HOST page's URL — which is the point, since
  // that page is the real `/resources`. `syncUrl` turns it off for a page where
  // something else owns the query string.
  //
  // This stays the ONLY writer of `type`/`q`/`sort`. External controls call the
  // setters and let the mirror follow; custom code that also writes the query
  // string will fight this effect.
  useEffect(() => {
    if (!syncUrl) return;
    const params = new URLSearchParams(window.location.search);
    if (filter !== "all") params.set("type", filterToSlug(filter));
    else params.delete("type");
    if (query) params.set("search", query);
    else params.delete("search");
    if (sort !== "latest") params.set("sort", sort);
    else params.delete("sort");
    // The search parameter used to be `q`. Both are still READ (see
    // `parseSearchParam`), but only `search` is written, and the old name is
    // cleared here so a link carrying both cannot leave a stale `q` behind in
    // the address bar after the visitor has typed something else.
    params.delete("q");
    const search = params.toString();
    window.history.replaceState(
      null,
      "",
      search
        ? `${window.location.pathname}?${search}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`,
    );
  }, [filter, query, sort, syncUrl]);

  // Any change to the result set restarts pagination. Sort counts: reversing
  // the order while 40 cards are revealed would otherwise show the 40 oldest
  // rather than returning to the top of the new order.
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [filter, query, sort, pageSize]);

  const filtered = useMemo(
    () => sortCards(filterCards(allCards, filter, query), sort),
    [allCards, filter, query, sort],
  );
  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // ── External control surface ─────────────────────────────────────────────
  // Kept current on every render so `getState()` never reports a stale value to
  // a caller outside React's world, which has no way to know a render is
  // pending. Assignment is idempotent — it derives purely from this render.
  const stateRef = useRef<ResourcesState>({
    filter,
    query,
    sort,
    count: 0,
    total: 0,
    visible: 0,
  });
  stateRef.current = {
    filter: filterToSlug(filter),
    query,
    sort,
    count: filtered.length,
    total: allCards.length,
    visible: visible.length,
  };

  // Registered once. The three setters are `useState` dispatchers and so are
  // referentially stable, which is what lets this effect depend only on the
  // callback and still hand out functions that never go stale.
  useEffect(() => {
    if (!onControlsReady) return;
    onControlsReady({
      setFilter: (value) => setFilter(parseFilterValue(value)),
      setQuery: (value) => setQuery(typeof value === "string" ? value : ""),
      setSort: (value) => setSort(parseSortValue(value)),
      reset: () => {
        setFilter("all");
        setQuery("");
        setSort("latest");
      },
      getState: () => ({ ...stateRef.current }),
    });
  }, [onControlsReady]);

  // Broadcast so Designer elements outside the embed can reflect the current
  // state — highlight the active tab, tick the sort option, print a count.
  // Without this the traffic is one-way and the controls drift out of step the
  // moment state changes from anywhere else (a `?type=` deep link, Load more,
  // or `reset()`).
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent<ResourcesState>(CHANGE_EVENT, {
        detail: { ...stateRef.current },
      }),
    );
  }, [filter, query, sort, filtered.length, visible.length]);

  const body = (
    <>
      <FiltersBar
        current={filter}
        query={query}
        resultCount={filtered.length}
        onFilterChange={setFilter}
        onQueryChange={setQuery}
      />

      {visible.length === 0 ? (
        <p className="resources-grid__empty">No resources match your search.</p>
      ) : (
        <div className="resources-grid">
          {visible.map((card) => (
            <CaseStudyCardDevlink key={card.id} {...cardPropsFor(card)} />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="resources-grid__load-more-wrap">
          <button
            type="button"
            className="load-more-button"
            onClick={() => setVisibleCount((n) => n + pageSize)}
          >
            Load more
          </button>
        </div>
      )}
    </>
  );

  // Embedded: contribute the three functional blocks and nothing else. The
  // `section` padding and `container` max-width below come from the Webflow
  // stylesheet and are what the HOST page is already applying to the region it
  // put the embed in — emitting them again nests one container inside another
  // and doubles the horizontal padding. The `#resources` id is dropped for the
  // same reason it always was: it is a likely anchor name on the host page.
  if (embedded) return body;

  return (
    <section className={cls(styles, "section")} id="resources">
      <div className={cls(styles, "container")}>{body}</div>
    </section>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────

export interface ResourcesIslandProps {
  allCards: ResourceCard[];
  initialFilter: FilterValue;
  initialQuery: string;
  heading: string;
  intro: string;
  /**
   * Suppress the heading block. Set when the app renders on a Webflow page that
   * already carries its own H1 and intro copy — two H1s on one rendered page is
   * both a duplication and an SEO smell.
   */
  hideHeading?: boolean;
  /**
   * Rendered inside another document's page (the Webflow Embed) rather than as
   * this app's own page. Two consequences, both about not fighting the host
   * document: the root is a `<div>` instead of a `<main>`, because that page
   * already has its landmarks, and a heading — if one is asked for at all —
   * renders as `<h2>` under the page's existing `<h1>`.
   */
  embedded?: boolean;
  /** Initial ordering. Defaults to newest first, matching the SSR sort. */
  initialSort?: SortValue;
  /**
   * Mirror filter/search/sort into the query string. On by default; the
   * embedding page passes `false` when something else there owns the URL.
   */
  syncUrl?: boolean;
  /** Cards per page, and per "Load more" click. */
  pageSize?: number;
  /**
   * Called once after mount with the handles that drive the grid from outside
   * React. Only `src/embed/entry.tsx` passes this, to publish them on
   * `window.ResourcesEmbed` for Designer elements sitting outside the embed
   * container.
   */
  onControlsReady?: (controls: ResourcesControls) => void;
}

export default function ResourcesIsland({
  allCards,
  initialFilter,
  initialQuery,
  heading,
  intro,
  hideHeading = false,
  embedded = false,
  initialSort = "latest",
  syncUrl = true,
  pageSize = DEFAULT_PAGE_SIZE,
  onControlsReady,
}: ResourcesIslandProps) {
  const Heading = embedded ? "h2" : "h1";

  const headingText = (
    <>
      <Heading className={cls(styles, "heading-style-h3", "text-left")}>
        {heading}
      </Heading>
      {intro && <p className="page-intro">{intro}</p>}
    </>
  );

  // Normally absent in embed mode — the Webflow page above the embed supplies
  // the H1 and intro copy, so `hideHeading` is set unless `data-heading` asks
  // for one. When it is asked for, it is flattened like the grid rather than
  // reintroducing a section/container the host page already provides.
  const headingBlock = hideHeading ? null : embedded ? (
    headingText
  ) : (
    <section className={cls(styles, "section")}>
      <div className={cls(styles, "container")}>{headingText}</div>
    </section>
  );

  const content = (
    <>
      {headingBlock}
      <ResourcesGrid
        initialFilter={initialFilter}
        initialQuery={initialQuery}
        initialSort={initialSort}
        allCards={allCards}
        pageSize={pageSize}
        syncUrl={syncUrl}
        embedded={embedded}
        onControlsReady={onControlsReady}
      />
    </>
  );

  return (
    <DevLinkProvider renderLink={renderLink}>
      {/*
        On the app's own page: a <main> landmark carrying the CSS scope, and —
        if the site chrome is ever wanted — the place to render a synced
        Navigation component (see the "Adding more DevLink components" section
        of README.md).

        Embedded: NO wrapper element. The host page already owns its landmarks,
        its section padding and its container width, and it is that page's
        layout the grid has to sit inside. So the island contributes only the
        functional blocks, and `entry.tsx` puts SCOPE_CLASS on the host
        container — which is why the app's styles still apply with nothing here
        to hang them on.
      */}
      {embedded ? content : <main className={SCOPE_CLASS}>{content}</main>}
    </DevLinkProvider>
  );
}
